import { FC, useEffect } from "react";
import { Tokens, useAuthStore } from "service";
import DashboardRouter from "./pages/dashboard/dashboard-router";
import AuthRouter, { AllowedRedirectsEnum } from "./pages/auth/auth-router";
import useSetup from "./hooks/use-setup";
import useOrganizationContextSync from "./hooks/use-organization-context-sync";
import LoadingPage from "./pages/loading-page";
import { BrowserRouter } from "react-router-dom";
import { AdRoutes } from "./constants/ad-routes";
import { queryClient } from "./query-client";

// DashboardRouter has no /accept-invite route, so an already-logged-in user
// clicking an invite link would otherwise hit its catch-all and silently
// drop the invite. Force a logout first so they land back on AuthRouter,
// which does have the route - see auth-router.tsx / accept-invite-page.tsx.
const isInviteLink = (): boolean => {
  const searchParams = new URLSearchParams(window.location.search);
  return (
    window.location.pathname === AdRoutes.acceptInvite ||
    searchParams.get("redirectTo") === AllowedRedirectsEnum.registerAdmin
  );
};

const App: FC = () => {
  const { isReady } = useSetup();
  useOrganizationContextSync();
  const { isLoggedIn, setIsLoggedIn, setCustomer } = useAuthStore();
  const forceLogoutForInvite = isReady && isLoggedIn && isInviteLink();

  useEffect(() => {
    if (forceLogoutForInvite) {
      Tokens.destroy();
      setIsLoggedIn(false);
      setCustomer(null);
      queryClient.clear();
    }
  }, [forceLogoutForInvite]);

  if (!isReady || forceLogoutForInvite) {
    return <LoadingPage />;
  }

  if (!isLoggedIn) {
    return (
      <BrowserRouter>
        <AuthRouter />
      </BrowserRouter>
    );
  }

  return <DashboardRouter />;
};

export default App;
